// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the review_comments VCA collection.
 *
 * Line-level review comments (inline code annotations on a PR/MR diff).
 * The parent review object lives in the `reviews` collection.
 *
 * Identity tuple: (gitConnectionId, repoId, commentId)
 * ID prefix: grc_
 */

import { z } from 'zod'

export const review_commentsSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Git connection reference ──────────────────────────────────────────
  gitConnectionId: z.string().max(100),
  codebaseId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  repoId: z.string().max(200),
  /** Provider-assigned comment identifier (globally unique within repo). */
  commentId: z.string().max(200),

  // ── Parent references ─────────────────────────────────────────────────
  /** FlowState reviews.id (FK) for cross-collection joins. */
  reviewId: z.string().max(100).optional(),
  /** PR/MR number this comment belongs to. */
  prNumber: z.number().int(),

  // ── Diff location ─────────────────────────────────────────────────────
  /** Relative path to the file being commented on. */
  path: z.string().max(1000),
  /** Line number in the diff. Null for file-level comments. */
  line: z.number().int().nullable().optional(),
  /** Which side of the diff the comment is on. */
  side: z.enum(['LEFT', 'RIGHT']),

  // ── Comment content ───────────────────────────────────────────────────
  body: z.string(),
  authorLogin: z.string().max(200),

  // ── Timestamps ───────────────────────────────────────────────────────
  createdAt: z.string().datetime(),

  // ── Provider-specific extras ──────────────────────────────────────────
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type Review_commentsInput = z.infer<typeof review_commentsSchema>
